/* eslint-disable no-inline-styles/no-inline-styles */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { observer } from 'mobx-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ReactComponent as LeftArrowIcon } from 'Assets/svg/left-arrow-thicker.svg';
import { Utils } from 'Utils/utils';
import './styles.scss';

const barChartColors = [
  '#F5AAA1',
  '#9DB6FB',
  '#92DFA4',
  '#F3A1C9',
  '#A1E5E3',
  '#D0B8F0',
  '#DFAD7F',
  '#ADBCCC',
  '#61B7B9',
  '#A08CBD',
  '#709CD9',
  '#F4CE90',
  '#95CDF4',
  '#C98789',
  '#A8A8A8',
  '#003f5c',
  '#2f4b7c',
  '#665191',
  '#a05195',
  '#d45087',
  '#f95d6a',
  '#ff7c43',
  '#ffa600',
];

const contactFields = [
  'First Name',
  'Last Name',
  'Email',
  'Linkedin Url',
  'Title',
  'Department1',
  'Department2',
  'Level',
  'City',
  'State',
  'Country',
  'Phone Direct',
  'Phone Mobile',
  'Phone Other',
  'Email Validation Status'
];

const companyFields = [
  'Website',
  'Company Name',
  'Company Street',
  'Company City',
  'Company State',
  'Company Country',
  'Company Zipcode',
  'Company Head Count',
  'Company Revenue',
  'Phone Company',
  'Industry',
  'Sector'
];

const chartOptions = {
  indexAxis: 'y',
  responsive: true,
  categoryPercentage: 0.9,
  datasets: {
    bar: {
      borderRadius: 2,
      barPercentage: 0.9,
      maxBarThickness: 30,
      skipNull: true,
    },
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      beginAtZero: true,
      ticks: {
        display: false,
      },
      grid: {
        drawBorder: false,
        display: false,
      },
    },
    y: {
      ticks: {
        color: '#6670A2',
        font: {
          size: 14,
        },
      },
      grid: {
        display: false,
      },
    },
  },
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'right',
      formatter: Math.round,
      font: {
        size: 14,
        lineHeight: 14,
      },
      color: '#6670A2',
    },
    legend: {
      display: false,
    },
    tooltip: { enabled: false },
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
);

function getChartData(fieldCount, fields) {
  const chartData = Object.keys(fieldCount).reduce(
    (output, key) => {
      if (fields.includes(key))
        return {
          labels: [...output.labels, key],
          datasets: [
            {
              ...output.datasets[0],
              data: [...output.datasets[0].data, fieldCount[key]],
            },
          ],
        };

      return output;
    },
    {
      labels: [],
      datasets: [
        { label: 'No. of contacts', data: [], backgroundColor: barChartColors },
      ],
    },
  );
  return chartData;
}

const FileEnrichmentReport = observer(props => {
  const { data, backAction } = props;
  const {
    enrichedCsvFile,
    totalRecord,
    enrichedRecord,
    fieldCount,
    creditUsed,
    enrichExclude = 0,
  } = data;

  const companyFieldsData = getChartData(fieldCount, companyFields);
  const contactFieldsData = getChartData(fieldCount, contactFields);

  const chartHeight =
    Math.max(companyFieldsData.labels.length, contactFieldsData.labels.length) *
      40 +
    40;

  const maxCompanyValue = Math.max.apply(
    null,
    companyFieldsData.datasets[0].data,
  );

  const maxContactValue = Math.max.apply(
    null,
    contactFieldsData.datasets[0].data,
  );

  const companyChartOptions = Utils.copyObjectWithoutReference(chartOptions);

  const contactChartOptions = Utils.copyObjectWithoutReference(chartOptions);

  companyChartOptions.scales.x.max = maxCompanyValue + maxCompanyValue * 0.3;

  contactChartOptions.scales.x.max = maxContactValue + maxContactValue * 0.3;

  const isContactsExcluded = enrichExclude !== null && enrichExclude > 0;

  return (
    <div className="file-enrichment-report">
      <div className="back-link-wrapper">
        <a onClick={backAction}>
          <span className="icon-wrap">
            <LeftArrowIcon className="left-arrow" />
          </span>
        </a>
        <h1>CSV Enrichment</h1>
      </div>
      <div className="report-card">
        <h2>Enrichment Report</h2>
        <div className="description">
          We have enriched your file with accurate and up-to-date information.
          Here&#39;s a quick summary,
        </div>
        <div className="stats-wrapper">
          <div className="stats-card">
            <span className="count">{Utils.numberFormat(enrichedRecord)}</span>
            <span className="text">
              {`${enrichedRecord > 1 ? 'Companies' : 'Company'} Enriched`}
            </span>
          </div>
          <div className="stats-card">
            <span className="count">{Utils.numberFormat(creditUsed)}</span>
            <span className="text">{`${
              creditUsed > 1 ? 'Contacts' : 'Contact'
            }  Enriched`}</span>
          </div>
        </div>
        <div className="note">
          <span className="span-mr note-title">Note:</span>
          <span className="note-text">
            <span>
              Personal, Disposable & Group Email addresses will not be enriched.
            </span>
            {isContactsExcluded && (
              // eslint-disable-next-line no-unwrapped-jsx/no-unwrapped-jsx-text
              <span>
                {Utils.numberFormat(enrichExclude)} out of{' '}
                {Utils.numberFormat(totalRecord)} Contact Information is not
                enriched.
              </span>
            )}
          </span>
        </div>
        <div className="charts-wrapper">
          {companyFieldsData.labels.length > 0 && (
            <div className="chart-wrapper">
              <div className="chart-title">Company Fields Enriched</div>
              <div className="chart-box" style={{ height: chartHeight }}>
                <Bar
                  options={companyChartOptions}
                  data={{
                    labels: companyFieldsData.labels,
                    datasets: companyFieldsData.datasets,
                  }}
                />
              </div>
            </div>
          )}
          {contactFieldsData.labels.length > 0 && (
            <div className="chart-wrapper">
              <div className="chart-title">Contact Fields Enriched </div>
              <div className="chart-box" style={{ height: chartHeight }}>
                <Bar
                  options={contactChartOptions}
                  data={{
                    labels: contactFieldsData.labels,
                    datasets: contactFieldsData.datasets,
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <a
          className="download-btn"
          type="file"
          href={enrichedCsvFile}
          target="_blank"
          rel="noreferrer"
        >
          Download
        </a>
      </div>
    </div>
  );
});

export default FileEnrichmentReport;
